import * as React from 'react';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PropTypes from 'prop-types';
import { useEffect, useState, useContext } from 'react';
import { getLeaderBoard } from '../apiCalls/partyAPI';
import AuthContext from '../contexts';
import { NotificationContext } from '../contexts/NotificationContext';
import '../styles/leaderboard.css';

const nameCellSx = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const correctCellSx = {
  width: '90px',
  minWidth: '90px',
  textAlign: 'right',
  whiteSpace: 'nowrap',
};

const expandCellSx = {
  width: '48px',
  minWidth: '48px',
  padding: '0 4px',
};

function Row(props) {
  const { row, isShowtime } = props;
  const [open, setOpen] = React.useState(false);

  const screamingSnakeToPretty = (screamingSnake) => {
    return screamingSnake.replace(/_/g, " ");
  }
  
  const totalCategories = 24; // Oscar categories
  const picksSet = row.picks ? row.picks.length : 0;
  const isComplete = picksSet === totalCategories;
  
  return (
    <React.Fragment>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell sx={expandCellSx}>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row" sx={nameCellSx} title={row.name}>
          {row.name}
        </TableCell>
        <TableCell sx={correctCellSx}>{row.correct}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              {isShowtime ? (
                <>
                  <Typography variant="h6" gutterBottom component="div">
                    Picks
                  </Typography>
                  <Table size="small" aria-label="picks">
                    <TableHead>
                      <TableRow>
                        <TableCell>Category</TableCell>
                        <TableCell>Pick</TableCell>
                        <TableCell>Correct</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {row.picks.map((pickRow, index) => (
                        <TableRow key={index}>
                          <TableCell component="th" scope="row">
                            {screamingSnakeToPretty(pickRow.category)}
                          </TableCell>
                          <TableCell>{pickRow.name}</TableCell>
                          <TableCell>{pickRow.correct === true ? 1 : 0}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              ) : (
                <Typography variant="body2" sx={{ color: '#d4af37', fontSize: '1rem', fontWeight: 600 }}>
                  {isComplete 
                    ? `✓ ${picksSet}/${totalCategories} picks set!` 
                    : `${picksSet}/${totalCategories} picks set`}
                </Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}


function LeaderBoard({partyId, isShowtime}) {
    const visible = { visibility: 'visible' };
    const hidden = { visibility: 'hidden' };
    const [modalShow, setModalShow] = useState(hidden);
    const [rows, setRows] = useState([]);
    const { user } = useContext(AuthContext);
    const { showToast } = useContext(NotificationContext);
   
    useEffect(() => {
        setModalShow(visible);
        if(partyId){
            getLeaderBoard(partyId, user.credential)
        .then(leaderboard => buildLeaderBoard(leaderboard))
        .finally(() => setModalShow(hidden))
        .catch(err => { if (!err.response) { showToast('Unable to reach server. Please try again.', 'error'); } else { showToast('Failed to load leaderboard. Please try again.', 'error'); } });
        }
    }, [partyId]);

    const buildLeaderBoard = (leaderBoard) => {
        leaderBoard.forEach(guest => {
          const name = guest.firstName ? guest.firstName+" "+guest.lastName : guest.email;
            setRows(rows => [...rows, { 
                name: name, 
                correct: guest.correct, 
                picks: guest.picks 
            }])
        });
    }

    return (
      <>
        <span className="loader" style={modalShow}></span>
        <TableContainer component={Paper} sx={{ padding: '1.5rem' }}>
          <Table
            aria-label="collapsible table"
            sx={{ width: '100%' }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={expandCellSx} />
                <TableCell>Party Person</TableCell>
                <TableCell sx={correctCellSx}>Correct Picks</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <Row key={index} row={row} isShowtime={isShowtime} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </>
      );
}

Row.propTypes = {
    row: PropTypes.any,
    isShowtime: PropTypes.bool
}

LeaderBoard.propTypes = {
    partyId: PropTypes.string,
    isShowtime: PropTypes.bool
}

export default LeaderBoard;
